import { atom } from 'jotai';

import { logEvent } from '@/pages/api/v1/utils';

import { SCENE_KEYS, type SceneKey } from './types';

import type { Plan } from '@/fixtures/plans';
import type { AssetGroup } from '@/types/asset';
import type { CheckoutPlanSlug } from './scenes/download/sections/usePlanPrices';
import type { VisitorPremiumDownloadVariant } from './visitorPremiumDownloadTest';

export type SlideDirection = 'forward' | 'backward';

const computeDirection = (from: string | null, to: string): SlideDirection => {
  if (from === null || from === to) {
    return 'forward';
  }
  const fromIdx = SCENE_KEYS.indexOf(from as SceneKey);
  const toIdx = SCENE_KEYS.indexOf(to as SceneKey);
  if (fromIdx === -1 || toIdx === -1) {
    return 'forward';
  }
  return fromIdx < toIdx ? 'forward' : 'backward';
};

export type StageState = {
  active: string | null;
  direction: SlideDirection;
};

export const stageAtom = atom<StageState>({
  active: null,
  direction: 'forward',
});

/**
 * True while any stage flow (download journey, auth, onboarding…) is open.
 * Page-level popup/dialogue queues read this to defer themselves so nothing
 * renders behind or on top of an active flow (DEV-6707).
 */
export const isDialogStageOpenAtom = atom(
  (get) => get(stageAtom).active !== null
);

export const openSceneAtom = atom(
  null,
  (get, set, key: string, opts?: { direction?: SlideDirection }) => {
    const { active: from } = get(stageAtom);
    set(stageAtom, {
      active: key,
      direction: opts?.direction ?? computeDirection(from, key),
    });

    // Scenes replaced ScreenProvider modals, which logged every screen change;
    // the sign-up/login funnel went dark without this (DEV-6834).
    if (from !== key && typeof window !== 'undefined') {
      void logEvent({
        event_type: 'modal_screen_view',
        asPath: window.location.pathname + window.location.search,
        metadata: { screen_name: key },
      });
    }

    if (
      (key === 'auth.login' || key === 'auth.signup-options') &&
      get(authEntrySceneAtom) === null
    ) {
      set(authEntrySceneAtom, key);
    }
  }
);

export const closeStageAtom = atom(null, (_get, set) => {
  set(stageAtom, (prev) => ({ ...prev, active: null }));
  set(loginReturnAtom, null);
  set(authDialogContextAtom, null);
  set(authEntrySceneAtom, null);
  set(pauseDownloadWarningAssetAtom, null);
  set(changePlanSelectionAtom, null);
  set(signupPlanStepAtom, null);
  // Deliberately NOT clearing `downloadStageContextAtom` here: the scene stays
  // mounted through the close animation (see `lastSceneRef` in index.tsx), so
  // nulling the asset now would flash the "Asset title" placeholder tile as the
  // dialog slides out. Every entry point sets a fresh context before reopening,
  // and nothing reads it while the stage is closed, so leaving it is safe.
});

export const authEntrySceneAtom = atom<
  'auth.login' | 'auth.signup-options' | null
>(null);

export const loginReturnAtom = atom<string | null>(null);

export const authDialogContextAtom = atom<string | null>(null);

/**
 * Arms of the free-asset download sign-up test (DEV-7278) that show plans:
 * `plan-step` shows `onboarding.plan-step` after the account is created,
 * `plan-choice` shows it as `download.plan-choice` before.
 */
export type DownloadSignupPlanArm = 'plan-step' | 'plan-choice';

/** Which journey the plan step is part of; tagged on its events. */
export type PlanStepFlow =
  | 'general'
  | `download-${DownloadSignupPlanArm}`
  // Variant A of the visitor premium download test (DEV-7217).
  | 'download-premium-visitor';

/**
 * Set when a sign-up lands in a plan-step Variant (DEV-7090, DEV-7278):
 * `onboarding.auth-gate` routes to `onboarding.plan-step` instead of the
 * questionnaire, and the plan step tags its events with the sign-up method.
 */
export const signupPlanStepAtom = atom<{
  method: 'google' | 'email';
  flow: PlanStepFlow;
} | null>(null);

/**
 * Referral link returned by a successful partner application, handed from
 * `partner.application` to `partner.success` (DEV-6760). Not cleared on
 * close for the same reason as `downloadStageContextAtom`: the scene stays
 * mounted through the close animation, and every entry sets a fresh value.
 */
export const partnerReferralLinkAtom = atom<string | null>(null);

/**
 * Whether the referral link is still being generated server-side
 * (`isReferralLinkPending` on the submission response, DEV-6870). When true
 * and no link is available, `partner.success` shows a pending message
 * instead of the copyable link.
 */
export const partnerReferralLinkPendingAtom = atom<boolean>(false);

export interface PauseDownloadWarningAsset {
  assetName: string;
  imageUrl?: string;
  isPremium: boolean;
  type: AssetGroup['type'];
}

export const pauseDownloadWarningAssetAtom =
  atom<PauseDownloadWarningAsset | null>(null);

export const changePlanSelectionAtom = atom<Plan['name'] | null>(null);

export type DownloadJourney = 'free' | 'premium';

export type DownloadUsage = 'own-content' | 'client-content';

/**
 * Asset family that opened the download flow. Mirrors the Download Gateway's
 * `AssetRef['kind']` so the eventual entry point maps 1:1. Drives the summary
 * tile's cover-art shape: audio kinds (`music`, `sfx`) render a circle, visual
 * kinds (`stock-video`, `mg`, `lut`) a horizontal rectangle (DEV-6514).
 */
export type DownloadAssetKind = 'music' | 'sfx' | 'mg' | 'lut' | 'stock-video';

/**
 * Display-level asset payload for the download scenes' summary tile. The
 * entry point that opens a `download.*` scene maps its richer asset object
 * (e.g. `AssetGroup` via `getImageData`) down to this before opening.
 */
export type DownloadStageAsset = {
  kind: DownloadAssetKind;
  artistName: string;
  assetName: string;
  imgAlt: string;
  imgSrc: string;
};

export type DownloadStageContext = {
  /** `null` renders the placeholder tile (used by admin previews). */
  asset: DownloadStageAsset | null;
  /**
   * The full asset the entry point was downloading. Powers the usage step's
   * "Download free" / "Try it free in your edit" CTAs, which start the real
   * download from inside the stage (DEV-6515). Absent in admin previews —
   * those CTAs then just close the stage.
   */
  sourceAsset?: AssetGroup;
  journey: DownloadJourney;
  usage?: DownloadUsage;
  /**
   * Plan chosen on the premium journey's usage step. The signup step reads
   * it to send the newly created account to that plan's checkout (DEV-6520).
   */
  plan?: CheckoutPlanSlug;
  /** Plan-showing arm of the download sign-up test (DEV-7278), if any. */
  signupPlanArm?: DownloadSignupPlanArm;
  /**
   * Page the user was on when they left for checkout. Set only when the
   * intent is persisted across the `/subscribe` redirect (DEV-6516) so the
   * payment-confirmation fallback's "Browse more" / post-download exit can
   * return them there rather than stranding them on the checkout page.
   */
  returnTo?: string;
  /**
   * Set when a visitor lands in a variant of the premium download test
   * (DEV-7217), so the sign-up scenes and their success routing follow that
   * variant's journey. Absent for control and everyone outside the test.
   */
  visitorTestVariant?: Exclude<VisitorPremiumDownloadVariant, 'control'>;
};

export const downloadStageContextAtom = atom<DownloadStageContext | null>(null);
